import { FilterGroup } from './filters.data'
import { flatten } from '../helpers/helpers'
import { PublicEventData, PublicTimelineItem } from './public.model';
import { CustomQuestion, JoiEvent, LabelMaps, MediaTerms, Ranking, Venue } from './events.data';
import { MediaTermState, Performer } from './performers.data';
import { Informable, InformableSection } from './informables.data';
import { OnsiteSingleTimeline } from './onsite.data';
import { Sponsor } from './sponsors.data';
import { WorkerWithProfile } from './workforce.data';
import { Exhibitor, ExhibitorSummary } from './exhibitors.data';
import { JoiFile } from './files.model';
import { getLinkableVenues, EntityRequest, ProgramLogisticsDisplayOptions } from './programs.data';
import { BrandingData, BrandingProps, HomePageData, LogisticsInBriefSelection, PublishInBriefSelection } from './publish.data';
import { PublicQuoteInfo } from './budget.data';
import { JoiResource } from './resources.data';
// TODO removed because of ts conflicts // import { Dictionary } from '@ngrx/entity';
import { Asset } from './assets.data';
import { Attendee } from './attendees.data';
import { BriefActions } from './brief.data';
import { Dictionary } from './app.data';

export interface ImageUploaderResult { photoUrl: string, thumbUrl: string, originalName: string }

export type ShareableItemType = "asset" | "oss" | "publish" | "profile" | "performer" | "stagingRequest" | "published_data"  | "attendee" | "attendeeBrief"
  | "submitPerformers" | "submitResources" | "submitExhibitors" | "submitAttendees" | "synopsis" | "sponsor" | "exhibitor" | "exhibitorBrief" | 'events' | "notes" | 'attendeesView'
  | "eAsset" | 'eRequest' | 'fullAsset' | 'resource' | 'resourceBrief' | 'quote' | 'revenue' | 'entityAsset' | "logistics" | "cues" | "information" 

export const ShareableItemTypes: { [k in ShareableItemType]: k } = { asset: 'asset', oss:'oss', publish:'publish', 
  profile:'profile', performer:'performer', exhibitor: 'exhibitor', exhibitorBrief: 'exhibitorBrief', synopsis: 'synopsis', attendee: 'attendee',
  stagingRequest: 'stagingRequest',sponsor: 'sponsor', published_data: 'published_data', events: 'events', notes: "notes", 
  eAsset: 'eAsset', eRequest: 'eRequest', fullAsset: 'fullAsset', resource: 'resource', resourceBrief: 'resourceBrief', 
  quote: 'quote', revenue: 'revenue', entityAsset: 'entityAsset', submitPerformers: 'submitPerformers', submitExhibitors: 'submitExhibitors',
  submitResources: 'submitResources', logistics: 'logistics', cues: 'cues', information: "information", attendeeBrief: 'attendeeBrief', submitAttendees: 'submitAttendees', attendeesView: 'attendeesView' }

export const allShareableTypes = Object.keys(ShareableItemTypes) as ShareableItemType[]

export const shareUrlsByType: { [k in ShareableItemType]: string } = { asset: 'session/assets', oss:'onsite/schedule', 
  publish:'publish', profile:'performer/profile', performer:'performer/brief', exhibitor: 'exhibitor/profile', logistics: 'production',
  exhibitorBrief: 'exhibitor/brief', synopsis: 'session/info', stagingRequest: 'session/staging',sponsor: 'sponsor/profile', 
  published_data: 'published_data', events: 'events', notes: "notes", eAsset: 'stand/assets', eRequest: 'stand/request', 
  fullAsset: 'asset', resource: 'resource/profile', resourceBrief: 'resource/brief', quote: 'quote', revenue: 'revenue', 
  submitPerformers: 'call/performers', submitResources: 'call/resources', submitExhibitors: 'call/exhibitors', submitAttendees: 'call/attendees',  entityAsset: 'entityAsset', cues: 'cues', information: "information", attendee: 'attendee/profile', attendeeBrief: 'attendee/brief', attendeesView: 'attendees/view' } as const

export type PublishStatus = 'published' | 'unpublished' | 'never published' 

export interface BasePublishInfo extends BrandingProps {
  title: string
  description: string
  status: PublishStatus
  publisher?: string
  publisherUid?: string
  publishedAt?: any
  creatorUid: string
  creator: string
  publishCode?: string
  excludedFieldIds: string[] // must have on creation now since some values are deselected (added to excluded fields) by default
  filters?: FilterGroup
  tabNames?: { [tId: string]: string }
  homePageData?: HomePageData
}

export interface PublishInfo extends BasePublishInfo {
  CTALinks?: JoiFile[]
  venueId: string
  eventId?: string // for use in event lists
  accountId?: string 
  createdAt?: Date
}

export type ProgramTab = 'sponsorsTab' | 'programTab' | 'exhibitorsTab' | 'performersTab' | 'homeTab'
export type DefaultTabName = 'Home' | 'Sponsors' | 'Program' | 'Exhibitors' | 'Performers'
export const getDefaultProgramTabNames = ():{ [tId in ProgramTab]: DefaultTabName } => ({ sponsorsTab: 'Sponsors', programTab: 'Program', exhibitorsTab: 'Exhibitors', performersTab: 'Performers', homeTab: 'Home'  })

export const makeDefaultPublishInfoExcludedFields = () => 
  ['eventName', 'publishedName', 'CTALinks', 'publishedDescription', 'viewMyTimezoneToggle' ]

export type PublicEditProfileStatusTypesType = "Off" | "Active" | "Submitted"
export const publicEditProfileStatusTypes: { [k in PublicEditProfileStatusTypesType]: k } = { Off:'Off', Active: 'Active', Submitted: 'Submitted' }

export interface PEPStati { Off, Active, Submitted }
export type PEPStatus = keyof PEPStati

export interface PerformerStati { NotInvited, Accepted, Pending, Declined }
export type PerformerStatus = keyof PerformerStati
export interface BriefStati { NotBriefed, Briefed }
export type BriefStatus = keyof BriefStati
export interface ReadyStati { Ready, NotReady }

export type ConfirmedStatus = 'Confirmed' | 'Not Confirmed'
export interface SponsorStati { Confirmed, Pending, Cancelled }
export type SponsorStatus = keyof SponsorStati
export type QuoteStatus = 'Accepted' | 'Pending' | 'Declined'

export type StatusType = PublicEditProfileStatusTypesType | BriefShareStatus | PerformerStatus | BriefStatus | ConfirmedStatus | SponsorStatus | ShareStatus | MediaTermState | 'Ready' | 'NotReady'

export const allStatusTypes: { [k in StatusType]: k } = { Off:'Off', Active: 'Active', Submitted: 'Submitted', On: 'On', 
  Accepted: 'Accepted', NotInvited: 'NotInvited', Pending: 'Pending', Declined: 'Declined', NotBriefed: 'NotBriefed', 
  Briefed: 'Briefed', Confirmed: 'Confirmed', 'Not Confirmed': 'Not Confirmed', Cancelled: 'Cancelled', shared: 'shared', Ready: 'Ready', 'NotReady': 'NotReady',
  'never shared': 'never shared', unshared: 'unshared', 'Not Accepted': 'Not Accepted' }

export interface BriefInfo {
  id: string;
  performerId?: string;
  exhibitorId?: string;
  resourceId?: string
  attendeeId?: string
  status: BriefShareStatus
}



export type BriefType = 'performer' | 'exhibitor' | 'resource' | 'attendee'
export const publicEditProfileUrlDirectory = { 'performer':'profile/', 'exhibitor': 'exhibitor/', 'resource': 'resource/', 'attendee': 'attendee/' }


export type CoreEntityType = BriefType | 'sponsor'
export const createEditProfileTypeToCoreEntityType: { [t in CreateEditProfileType]: CoreEntityType } = {
  'profile': 'performer', 'exhibitor': 'exhibitor', 'resource': 'resource', 'sponsor': 'sponsor', 'attendee': 'attendee'
}

export interface PublicEditEntityAssetsInfo {
  code: string
  entityId: string
  eventId: string
  entityType: CoreEntityType
  status: PublicEditProfileStatusTypesType
  addNew: boolean
  editableAssetIds: string[]
}

export type CreateEditProfileType = 'profile' | 'sponsor' | 'exhibitor' | 'resource' | 'attendee'
export interface CreateEditProfileCodeData { // for use in posting to create-edit-profile-code only
  entityId: string
  eventId: string
  entityType: CreateEditProfileType
}

export interface PublicEditProfileInfo { 
  code: string;
  performerId?: string;
  sponsorId?: string;
  exhibitorId?: string
  resourceId?: string
  attendeeId?: string
  status: PublicEditProfileStatusTypesType
}

export interface PublicPresentationAssetData {
  eventTitle: string
  sessionTitle: string
  performerName: string
  eventId: string
  brandingData: BrandingData
}

export interface PublicStagingRequestData extends PublicPresentationAssetData {
  stagingRequest: EntityRequest
}

export interface PublicExhibitorAssetData {
  eventTitle: string
  standTitle: string
  exhibitorName: string
  eventId: string
  brandingData: BrandingData
}

export interface PublicExhibitorRequestData extends PublicExhibitorAssetData {
  request: EntityRequest
}

export type OffOnStatus = "Off" | "On"
export interface PublicNotesInfo {
  publishCode: string
  eventId: string
  programId: string
  status: OffOnStatus
}

export interface AttendeesViewInfo {
  publishCode: string
  eventId: string
  status: OffOnStatus
}

// tslint:disable-next-line
export interface PublicCuesInfo extends PublicNotesInfo {
  
}

export interface EditProfileQueryData {
  entityType: CreateEditProfileType
  publishCode: string
}

export interface EditProfileFetchedData {
  entity: Sponsor | Performer | Exhibitor
  eventId: string
  eventName?: string
  entityType: CreateEditProfileType
  oldFiles?: JoiFile[]
  newFiles?: JoiFile[]
  customQuestions?: CustomQuestion[]
  brandingData?: BrandingData
}

export interface SubmitEditProfileData extends EditProfileFetchedData {
  publishCode: string
}

export interface EditResourceFetchedData {
  resource: JoiResource
  eventName?: string
  eventId: string
  customQuestions?: CustomQuestion[]
  brandingData?: BrandingData
}
export interface SubmitEditResourceData extends EditResourceFetchedData {
  publishCode: string
}

export interface SubmitEntityData {
  entity?: Performer | JoiResource
  entityType: CoreEntityType
  eventId: string
  submitTemplateId: string
}

export interface SubmitEntityExtraData {
  eventTitle: string
  templateTitle: string
  instructions: string
  customQuestions: CustomQuestion[]
  brandingData: BrandingData
}

export type ShareStatus = 'shared' | 'unshared' | 'never shared'
export type ItemStatus = "Off" | "Active" | "Submitted"
export type BriefShareStatus = 'Off' | 'On' 
export type PerformerBriefMediaStatus = "Off" | "On"
export const itemStatus: ItemStatus[] = ['Active', 'Submitted']

export const briefStatusButtonNames = { Off: 'Off', On: 'On'  }
export const briefStatusOptions: BriefShareStatus[] = ['Off', 'On']
export const briefStatusColors = { Off: 'gray', On: 'green'  }

export const performerBriefMediaStatus: PerformerBriefMediaStatus[] = ['On', 'Off']

export interface BriefQueryData {
  shareableId: string
}

export interface AcceptMediaTermsData {
  id: string
  type: BriefType
  termsId: string
}

export interface BriefBaseShareableData {
  eventTitle: string
  eventId: string
  mediaTerms: MediaTerms[]
  performers: { [pId: string]: Performer }
  sponsors: { [key: string]: Sponsor }
  sponsorTypes: { [tId:string]: Ranking }
  labelMaps: Partial<LabelMaps>
  extraShareables?:Partial<ShareableState>
  singleEntityInformable: InformableSection[]
  allEntitiesEventInformable: InformableSection[]
  timeline: OnsiteSingleTimeline[]
  workforceContactProfile: WorkerWithProfile
  workforceContactInactive: boolean
  exhibitorSummaries: ExhibitorSummary[]
  venues: { [id: string]: Venue }
  type: 'data'
  briefActions: BriefActions
  briefProgramPublishes: PublishInBriefSelection[]
  briefLogisticsViews: LogisticsInBriefSelection[]
  briefOnsiteViews: LogisticsInBriefSelection[]
  performer: any // temp fix for console typescript errors.  Stupid thing can't figure it out yet
  exhibitor: any
  resource: any
  debugData?: any
  entityTypes: Ranking[]
  brandingData: BrandingData
  performerRoles: Dictionary<Ranking>
}

export interface PBD extends BriefBaseShareableData {
  performer: Performer
  briefType: 'performer'
}
export interface EBD extends BriefBaseShareableData {
  exhibitor: Exhibitor
  briefType: 'exhibitor'
}
export interface RBD extends BriefBaseShareableData {
  resource: JoiResource
  briefType: 'resource'
}
export interface ABD extends BriefBaseShareableData {
  attendee: Attendee
  briefType: 'attendee'
}


export type BriefShareableData = PBD | EBD | RBD | ABD

export const makeDefaultShareOSSInfoExcludedFields = () => 
  ['performerLabels', 'sponsorLabels',]

export interface ShareOSSInfo {
  title: string;
  eventTitle?: string;
  eventId?: string;
  id: string
  description: string;
  status: ShareStatus;
  excludedFieldIds?: string[];
  filters?: FilterGroup;
}

export const makeDefaultShareLogisticsExcludedFields = (): ProgramLogisticsDisplayOptions[] => [] 

export interface LogisticsShareable extends ShareableItemBase {
  type: 'logistics'
  title: string
  description: string
  eventId: string
  status: ShareStatus
  excludedFieldIds: string[]
  filters: FilterGroup
}


export interface EntityItemBase {
  id: string;
  key: string;
  status: ItemStatus
}

export interface StandItemInfo extends EntityItemBase {
  standId: string
  exhibitorId: string
}

export interface SessionItemInfo extends EntityItemBase {
  sessionId: string;
  programId: string;
  performerId: string;
  performerName?: string; // this is needed for synopsis, an ok for the others to have too
}



export interface ShareableState {
  cues: {[code:string]: PublicCuesInfo }
  publish: {[code:string]:PublishInfo }
  profile: {[performerId:string]:PublicEditProfileInfo}
  asset: {[key:string]:SessionItemInfo }
  entityAsset: { [key:string]: PublicEditEntityAssetsInfo }
  eAsset: {[key:string]:StandItemInfo}
  oss: {[id:string]:ShareOSSInfo}
  sponsor: {[sponsorId: string]:PublicEditProfileInfo}
  performer: {[id:string]:BriefInfo}
  exhibitorBrief: {[id:string]:BriefInfo}
  published_data: {[code:string]: PublicEventData }
  synopsis: {[code:string]: SessionItemInfo}
  stagingRequest: {[code:string]: SessionItemInfo}
  eRequest: {[code:string]: StandItemInfo}
  exhibitor: {[exhibitorId: string]:PublicEditProfileInfo}
  resource: {[resourceId: string]:PublicEditProfileInfo}
  attendee: {[attendeeId: string]: PublicEditProfileInfo}
  attendeeBrief: {[id:string]:BriefInfo}
  attendeeView: { [id:string]:AttendeesViewInfo}
  resourceBrief: {[id:string]:BriefInfo}
  notes: { [programId: string]: PublicNotesInfo }
  quote: { [quoteId: string]: PublicQuoteInfo }
  logistics: { [code: string]: LogisticsShareable }
}

export type ShareableInfo = PublishInfo | PublicEditProfileInfo | ShareOSSInfo | SessionItemInfo | BriefInfo | PublicNotesInfo | StandItemInfo | PublicQuoteInfo | AttendeesViewInfo

export const ShareableInfoKeyMap: { [k in ShareableItemType]: string } = {
  publish: 'publishCode',
  oss: 'id',
  profile: 'performerId',
  asset: 'key',
  entityAsset: 'entityId',
  fullAsset: null, // ASSET IS NOT A REAL SHAREABLE, BUT IT NEEDS TO BE IN HERE FOR THE SHARE CODE COMPONENTS
  submitPerformers: null, // NOT A REAL SHAREABLE, BUT IT NEEDS TO BE IN HERE FOR THE SHARE CODE COMPONENTS
  submitResources: null, // NOT A REAL SHAREABLE, BUT IT NEEDS TO BE IN HERE FOR THE SHARE CODE COMPONENTS
  submitExhibitors: null, // NOT A REAL SHAREABLE, BUT IT NEEDS TO BE IN HERE FOR THE SHARE CODE COMPONENTS
  submitAttendees: null,
  information: null, // toggled on or off on event, so not a shareable
  stagingRequest: 'key',
  eAsset: 'key',
  eRequest: 'key',
  synopsis: 'sessionId',
  performer: 'performerId',
  sponsor: 'sponsorId',
  attendee: 'attendeeId',
  attendeeBrief: 'attendeeId',
  attendeesView: 'eventId',
  exhibitor: 'exhibitorId',
  exhibitorBrief: 'exhibitorId',
  resource: 'resourceId',
  resourceBrief: 'resourceId',
  published_data: 'key',
  events: 'publishCode',
  notes: 'programId',
  cues: 'programId',
  quote: 'quoteId',
  logistics: null,
  revenue: null
}

export interface ShareableItemBase {
  code: string
  accountId?: string // currently only used for program publish
  eventId: string
  storagePath?: string
  timestamp?: any
  type: ShareableItemType
  info?: ShareableInfo
  noURLs?: boolean
}

interface ShareablePublish extends ShareableItemBase {
  type: 'publish'
  info: PublishInfo
}
export interface ShareableOSS extends ShareableItemBase {
  type: 'oss'
  info: ShareOSSInfo
}
interface ShareableProfile extends ShareableItemBase {
  type: 'profile' | 'sponsor' | 'exhibitor' | 'resource' | 'attendee'
  info: PublicEditProfileInfo
}
interface ShareableBrief extends ShareableItemBase {
  type: 'performer' | 'exhibitorBrief' | 'resourceBrief' | 'attendeeBrief'
  info: BriefInfo
}

export interface ShareableQuote extends ShareableItemBase {
  type: 'quote'
  info: PublicQuoteInfo
}

interface ShareablePublishedEvent extends ShareableItemBase {
  type: 'published_data'
}
interface ShareableEvent extends ShareableItemBase {
  type: 'events'
}
interface ShareableNotes extends ShareableItemBase {
  type: 'notes' | 'cues'
  info: PublicNotesInfo
}

export type StandItemType = 'eAsset' | 'eRequest' 
interface ShareableStandInfo extends ShareableItemBase {
  type: StandItemType
  info: StandItemInfo
}

export type SessionItemType = 'asset' | 'synopsis' | 'stagingRequest'
interface ShareableSessionInfo extends ShareableItemBase {
  type: SessionItemType
  info: SessionItemInfo
}

export interface ShareableEntityAssets extends ShareableItemBase {
  type: 'entityAsset'
  info: PublicEditEntityAssetsInfo
}

export interface ShareableAttendeesView extends ShareableItemBase {
  type: 'attendeesView'
  info: AttendeesViewInfo
}

export type ShareableItem = ShareablePublish | ShareableOSS | ShareableProfile | ShareableBrief | ShareableEntityAssets | ShareableAttendeesView |
  ShareablePublishedEvent | ShareableEvent | ShareableNotes | ShareableStandInfo | ShareableSessionInfo | ShareableQuote | LogisticsShareable

export const getLinkedVenue = (event: JoiEvent, excludedFieldIds: string[], venueId: string, filteredTimeline: PublicTimelineItem[][]): Venue => {
  if (excludedFieldIds && excludedFieldIds.includes('primaryVenue')) return null
  const venue = event.venues[venueId]
  if (venue && venue.googleId && venue.venue) return venue

  const usedVenueIds = new Set(flatten(filteredTimeline).map(item => item.program.venueId))
  const firstLinkableVenue = getLinkableVenues(event).filter(vn => usedVenueIds.has(vn.id))[0] || null
  return firstLinkableVenue
}

export interface SynopsisReturnData {
  eventTitle: string
  sessionTitle: string
  performerName: string
  programName: string
  sessionDateTime: { primary: string, secondary: string }
  sessionDescription: string
  publicLinks: JoiFile[]
  eventId: string
  brandingData: BrandingData
}

export interface EventInformationData {
  eventName: string
  brandingData: BrandingData
  informable: Informable
  assets: Dictionary<Asset>
}